import type { FormDefinitionBuilderValue } from '@/components/appointment-forms/FormDefinitionBuilder';
import type { FormItem, FormItemType, FormSection } from '@/types/appointmentForm';
import type {
  CreateFormTemplateInput,
  FormSurface,
  FormTemplate,
  FormTemplateItemInput,
  FormTemplatePolicyInput,
  FormTemplateSectionInput,
  UpdateFormTemplateInput,
  UpsertFormAssignmentInput,
} from '@/types/formsEngine';
import { FILE_UPLOAD_BOUNDS, TIME_MINUTE_STEPS } from './field-types';
import {
  buildPublicSettings,
  defaultPublicSettingsDraft,
  publicSettingsDraftFrom,
  validatePublicSettings,
} from './public-settings';

const SURFACE_POLICY: Record<FormSurface, Required<FormTemplatePolicyInput>> = {
  CHECKLIST: {
    defaultVisibility: 'INTERNAL',
    multiplicity: 'SINGLE',
    completionGranularity: 'ITEM',
    instantiation: 'AUTO_ON_ATTACH',
    publicFillable: false,
    offlineCapable: true,
    notifyOnComplete: false,
  },
  VISIT_FORM: {
    defaultVisibility: 'INTERNAL',
    multiplicity: 'SINGLE',
    completionGranularity: 'INSTANCE',
    instantiation: 'ON_DEMAND',
    publicFillable: false,
    offlineCapable: false,
    notifyOnComplete: true,
  },
  BOOKING: {
    defaultVisibility: 'INTERNAL',
    multiplicity: 'SINGLE',
    completionGranularity: 'INSTANCE',
    instantiation: 'ON_DEMAND',
    publicFillable: true,
    offlineCapable: false,
    notifyOnComplete: false,
  },
  INTAKE: {
    defaultVisibility: 'CUSTOMER_VISIBLE',
    multiplicity: 'MULTIPLE',
    completionGranularity: 'INSTANCE',
    instantiation: 'ON_DEMAND',
    publicFillable: true,
    offlineCapable: false,
    notifyOnComplete: true,
  },
};

const PUBLIC_TYPES = new Set<FormItemType>([
  'TEXT',
  'NUMBER',
  'CHECKBOX',
  'YES_NO',
  'SINGLE_SELECT',
  'MULTI_SELECT',
  'DATE',
  'TIME',
  'ADDRESS',
  'FILE_UPLOAD',
]);

/**
 * Types limited to particular surfaces (mirrors `surfaces` in
 * `@notifi/shared-types/form-items`). FILE_UPLOAD needs the public intake
 * upload route, so it exists only on INTAKE.
 */
const SURFACE_LIMITED_TYPES: Partial<Record<FormItemType, readonly FormSurface[]>> = {
  FILE_UPLOAD: ['INTAKE'],
};

/**
 * Whether a builder item type may be placed on a surface: public surfaces take
 * only public-capable types, and surface-limited types only their surfaces.
 */
export function isItemTypeAllowedOnSurface(type: FormItemType, surface: FormSurface): boolean {
  const surfaces = SURFACE_LIMITED_TYPES[type];
  if (surfaces && !surfaces.includes(surface)) return false;
  if (surface !== 'BOOKING' && surface !== 'INTAKE') return true;
  return PUBLIC_TYPES.has(type);
}

function outOfRange(value: number | undefined, bounds: { min: number; max: number }): boolean {
  return (
    value !== undefined && (!Number.isInteger(value) || value < bounds.min || value > bounds.max)
  );
}

export function createUnifiedBuilderValue(surface: FormSurface): FormDefinitionBuilderValue {
  const intakeItems: FormItem[] = [
    {
      label: 'Name',
      type: 'TEXT',
      required: true,
      sortOrder: 0,
      config: {
        textStyle: 'short',
        inputType: 'text',
        systemField: 'customer_name',
        locked: true,
        placeholder: 'Full name',
      },
    },
    {
      label: 'Email',
      type: 'TEXT',
      required: true,
      sortOrder: 1,
      config: {
        textStyle: 'short',
        inputType: 'email',
        systemField: 'customer_email',
        locked: true,
        placeholder: 'name@example.com',
      },
    },
    {
      label: 'Phone number',
      type: 'TEXT',
      required: true,
      sortOrder: 2,
      config: {
        textStyle: 'short',
        inputType: 'tel',
        systemField: 'customer_phone',
        locked: true,
        placeholder: '(555) 123-4567',
      },
    },
  ];
  return {
    name: '',
    description: '',
    defaultVisibility: SURFACE_POLICY[surface].defaultVisibility,
    // One empty section, matching exactly what "Add section" would build. A
    // template needs at least one, so starting from none means every single
    // person presses the same button before they can do anything. What it does
    // NOT come with is an item: a seeded question is a guess about the shape of
    // someone else's form, which they then have to rename or delete. An intake
    // form is the exception: its contact details are the same every time.
    sections:
      surface === 'INTAKE'
        ? [{ name: 'Contact details', sortOrder: 0, repeatable: false, items: intakeItems }]
        : [{ name: 'Section 1', sortOrder: 0, repeatable: false, items: [] }],
    // The common case for a visit document, and the safe one: it imposes none
    // of the public runtime's restrictions until someone asks for them.
    completedBy: 'TECHNICIAN',
    jobTypeIds: [],
    bookableServiceIds: [],
    allowMultipleSubmissions: surface === 'INTAKE',
    blocksCompletion: false,
    ...(surface === 'INTAKE' ? { publicSettings: defaultPublicSettingsDraft() } : {}),
  };
}

export function unifiedBuilderValueFromTemplate(
  template: FormTemplate,
): FormDefinitionBuilderValue {
  return {
    name: template.name,
    description: template.description ?? '',
    defaultVisibility: template.defaultVisibility,
    sections: template.sections.map((section) => ({
      id: section.id,
      placementKey: section.placementKey,
      sourceSectionVersionId: section.sourceSectionVersionId ?? undefined,
      name: section.name,
      sortOrder: section.sortOrder,
      repeatable: section.isRepeatable,
      repeatOptions: section.repeatOptions,
      items: section.items.map((item) => ({
        id: item.id,
        placementKey: item.placementKey,
        questionId: item.questionId ?? undefined,
        questionVersionId: item.questionVersionId ?? undefined,
        isLibraryItem: item.isLibraryItem,
        label: item.label,
        type: item.type === 'PHOTO' ? 'IMAGE_UPLOAD' : item.type,
        required: item.required,
        sortOrder: item.sortOrder,
        description: item.description,
        photoRequired: item.photoRequired,
        minPhotos: item.minPhotos,
        options: item.config?.options,
        config: item.config,
      })),
    })),
    jobTypeIds: template.assignments
      .filter((assignment) => assignment.targetType === 'JOB_TYPE' && assignment.targetId)
      .map((assignment) => assignment.targetId as string),
    bookableServiceIds: template.assignments
      .filter((assignment) => assignment.targetType === 'BOOKABLE_SERVICE' && assignment.targetId)
      .map((assignment) => assignment.targetId as string),
    allowMultipleSubmissions: template.multiplicity === 'MULTIPLE',
    blocksCompletion: template.assignments.some((assignment) => assignment.blocksCompletion),
    // Read back rather than defaulted. Falling through to TECHNICIAN on edit
    // meant opening a customer form in Settings and fixing a typo re-assigned
    // it to technicians — and every link already sent for it started failing.
    completedBy:
      template.publicFillable ||
      template.assignments.some((assignment) => assignment.audience === 'CUSTOMER')
        ? 'CUSTOMER'
        : 'TECHNICIAN',
    ...(template.surface === 'INTAKE'
      ? { publicSettings: publicSettingsDraftFrom(template.publicSettings) }
      : {}),
  };
}

/**
 * A problem, and where in the form it is. The location is what lets the builder
 * put the message on the field that caused it rather than only in a list — a
 * list at the top of a long form is a list the person pressing Save cannot see.
 */
export interface BuilderIssue {
  message: string;
  /** Present when the problem belongs to a particular section. */
  sectionIndex?: number;
  /** Present when it belongs to a particular item within that section. */
  itemIndex?: number;
  /** Present when it belongs to the template itself rather than its contents. */
  field?: 'name' | 'sections';
}

export function locateUnifiedBuilderIssues(
  value: FormDefinitionBuilderValue,
  surface: FormSurface,
): BuilderIssue[] {
  const issues: BuilderIssue[] = [];
  if (!value.name.trim()) {
    issues.push({ message: 'Template name is required.', field: 'name' });
  }
  if (value.sections.length === 0) {
    issues.push({ message: 'Add at least one section.', field: 'sections' });
  }

  value.sections.forEach((section, sectionIndex) => {
    const sectionLabel = `Section ${sectionIndex + 1}`;
    const at = (message: string) => issues.push({ message, sectionIndex });
    if (!section.name.trim()) at(`${sectionLabel} needs a name.`);
    if (section.items.length === 0) at(`${sectionLabel} needs at least one item.`);
    if (section.repeatable && !(section.repeatOptions ?? []).some((option) => option.trim())) {
      at(`${sectionLabel} needs at least one named repetition.`);
    }
    // A customer gets one copy per named repetition on the form page, the same
    // as a technician. A booking survey and an intake form are rendered inline
    // by flows that cannot expand them.
    if ((surface === 'BOOKING' || surface === 'INTAKE') && section.repeatable) {
      at(`${sectionLabel} cannot repeat in a public customer form.`);
    }

    section.items.forEach((item, itemIndex) => {
      const itemLabel = `${sectionLabel}, item ${itemIndex + 1}`;
      const onItem = (message: string) => issues.push({ message, sectionIndex, itemIndex });
      if (!item.label.trim()) onItem(`${itemLabel} needs a question.`);
      const options = item.config?.options ?? item.options ?? [];
      if (
        (item.type === 'SINGLE_SELECT' || item.type === 'MULTI_SELECT') &&
        new Set(options.map((option) => option.trim()).filter(Boolean)).size < 2
      ) {
        onItem(`${itemLabel} needs at least two choices.`);
      }
      if (item.type === 'MEASUREMENT' && !(item.config?.units ?? []).length) {
        onItem(`${itemLabel} needs at least one unit.`);
      }
      if (
        item.config?.min !== undefined &&
        item.config?.max !== undefined &&
        item.config.min > item.config.max
      ) {
        onItem(`${itemLabel} has a minimum greater than its maximum.`);
      }
      if (item.type === 'TIME' && item.config?.minuteStep !== undefined) {
        if (!TIME_MINUTE_STEPS.includes(item.config.minuteStep)) {
          onItem(`${itemLabel} needs a time interval of 5, 10, 15, 30 or 60 minutes.`);
        }
      }
      if (item.type === 'FILE_UPLOAD') {
        if (outOfRange(item.config?.maxFiles, FILE_UPLOAD_BOUNDS.maxFiles)) {
          onItem(`${itemLabel} allows 1 to 10 files.`);
        }
        if (outOfRange(item.config?.maxFileSizeMb, FILE_UPLOAD_BOUNDS.maxFileSizeMb)) {
          onItem(`${itemLabel} needs a file size limit from 1 to 25 MB.`);
        }
      }
      if (!isItemTypeAllowedOnSurface(item.type, surface)) {
        const context =
          surface === 'BOOKING'
            ? 'public booking'
            : surface === 'INTAKE'
              ? 'public customer forms'
              : surface === 'CHECKLIST'
                ? 'checklists'
                : 'visit forms';
        onItem(`${itemLabel} uses a type that is not available in ${context}.`);
      }
    });
  });

  if (surface === 'INTAKE' && value.publicSettings) {
    issues.push(...validatePublicSettings(value.publicSettings).map((message) => ({ message })));
  }

  return issues;
}

export function validateUnifiedBuilder(
  value: FormDefinitionBuilderValue,
  surface: FormSurface,
): string[] {
  return locateUnifiedBuilderIssues(value, surface).map((issue) => issue.message);
}

export function buildCreateTemplateInput(
  value: FormDefinitionBuilderValue,
  surface: FormSurface,
): CreateFormTemplateInput {
  return {
    name: value.name.trim(),
    description: value.description.trim() || null,
    surface,
    sections: buildSections(value.sections, surface),
    ...buildPolicy(value, surface),
    ...publicSettingsInput(value, surface),
  };
}

export function buildUpdateTemplateInput(
  value: FormDefinitionBuilderValue,
  template: FormTemplate,
): UpdateFormTemplateInput {
  return {
    name: value.name.trim(),
    description: value.description.trim() || null,
    sections: buildSections(value.sections, template.surface),
    ...buildPolicy(value, template.surface, template),
    ...publicSettingsInput(value, template.surface),
  };
}

/** `publicSettings` exists only on INTAKE; the server rejects it on every other surface. */
function publicSettingsInput(
  value: FormDefinitionBuilderValue,
  surface: FormSurface,
): Pick<CreateFormTemplateInput, 'publicSettings'> {
  if (surface !== 'INTAKE') return {};
  return {
    publicSettings: buildPublicSettings(value.publicSettings ?? defaultPublicSettingsDraft()),
  };
}

export function buildTemplateAssignments(
  value: FormDefinitionBuilderValue,
  surface: FormSurface,
  /** The template as saved, when editing. Its links keep their own settings. */
  current?: FormTemplate,
): UpsertFormAssignmentInput[] {
  if (surface === 'INTAKE') {
    return [
      {
        targetType: 'GLOBAL',
        targetId: null,
        audience: 'CUSTOMER',
        blocksCompletion: false,
        sortOrder: 0,
      },
    ];
  }

  const targetIds = surface === 'BOOKING' ? value.bookableServiceIds : value.jobTypeIds;
  const targetType = surface === 'BOOKING' ? 'BOOKABLE_SERVICE' : 'JOB_TYPE';
  // A booking survey is customer-facing by definition; everything else now says
  // so explicitly rather than defaulting to ANY, which assigned a checklist to
  // nobody in particular.
  const audience = surface === 'BOOKING' ? 'CUSTOMER' : (value.completedBy ?? 'TECHNICIAN');
  const blocksCompletion = surface === 'BOOKING' ? false : value.blocksCompletion;

  if (targetIds.length === 0) {
    if (surface === 'BOOKING') return [];
    return [
      {
        targetType: 'GLOBAL',
        targetId: null,
        audience,
        blocksCompletion,
        sortOrder: 0,
      },
    ];
  }

  // Save replaces the template's links wholesale, so a link that already exists
  // has to be sent back as it is: who fills it in on that job type, whether it
  // is attached to every visit, where it sorts. Rebuilding every link from the
  // builder's one set of defaults quietly turned auto-attach off and flattened
  // per-job-type choices whenever someone fixed a typo in the questions.
  const existing = new Map(
    (current?.assignments ?? [])
      .filter((assignment) => assignment.targetType === targetType && assignment.targetId)
      .map((assignment) => [assignment.targetId as string, assignment]),
  );
  return targetIds.map((targetId, sortOrder) => {
    const kept = existing.get(targetId);
    if (!kept) return { targetType, targetId, audience, blocksCompletion, sortOrder };
    return {
      targetType,
      targetId,
      audience: kept.audience,
      blocksCompletion: kept.blocksCompletion,
      sortOrder: kept.sortOrder,
      instantiationOverride: kept.instantiationOverride,
      autoAttach: kept.autoAttach ?? null,
    };
  });
}

function buildPolicy(
  value: FormDefinitionBuilderValue,
  surface: FormSurface,
  current?: FormTemplate,
): Required<FormTemplatePolicyInput> {
  const defaults = SURFACE_POLICY[surface];
  // A customer only ever reaches a form through the public route, and that
  // route refuses anything not opened to it — so answering "the customer
  // fills this in" has to open it, or the form silently never arrives.
  // Offline goes off with it: they fill it on their own device, not from the
  // technician's cache.
  const customerFilled = surface !== 'BOOKING' && value.completedBy === 'CUSTOMER';
  return {
    defaultVisibility:
      value.defaultVisibility ?? current?.defaultVisibility ?? defaults.defaultVisibility,
    // Intake has no toggle: every response shares one subject, so the server
    // refuses SINGLE. A template stored before that rule reads back as SINGLE;
    // saving it must not replay that.
    multiplicity: surface === 'INTAKE' || value.allowMultipleSubmissions ? 'MULTIPLE' : 'SINGLE',
    completionGranularity: current?.completionGranularity ?? defaults.completionGranularity,
    instantiation: current?.instantiation ?? defaults.instantiation,
    publicFillable: customerFilled || (current?.publicFillable ?? defaults.publicFillable),
    offlineCapable: customerFilled ? false : (current?.offlineCapable ?? defaults.offlineCapable),
    notifyOnComplete: current?.notifyOnComplete ?? defaults.notifyOnComplete,
  };
}

function buildSections(sections: FormSection[], surface: FormSurface): FormTemplateSectionInput[] {
  return sections.map((section, sectionIndex) => ({
    ...(section.id ? { id: section.id } : {}),
    ...(section.sourceSectionVersionId
      ? { sourceSectionVersionId: section.sourceSectionVersionId }
      : {}),
    name: section.name.trim(),
    sortOrder: sectionIndex,
    isRepeatable:
      surface === 'BOOKING' || surface === 'INTAKE' ? false : Boolean(section.repeatable),
    repeatOptions:
      surface === 'BOOKING' || surface === 'INTAKE' || !section.repeatable
        ? []
        : (section.repeatOptions ?? []).map((option) => option.trim()).filter(Boolean),
    items: section.items.map((item, itemIndex) => buildItem(item, itemIndex)),
  }));
}

function buildItem(item: FormItem, sortOrder: number): FormTemplateItemInput {
  const type = item.type === 'IMAGE_UPLOAD' ? 'PHOTO' : item.type;
  const options = item.config?.options ?? item.options;
  const config = {
    ...(item.config ?? {}),
    ...(options?.length ? { options } : {}),
  };
  return {
    ...(item.id ? { id: item.id } : {}),
    ...(item.questionVersionId ? { questionVersionId: item.questionVersionId } : {}),
    label: item.label.trim(),
    type,
    required: item.required,
    sortOrder,
    description: item.description?.trim() || null,
    photoRequired: Boolean(item.photoRequired),
    minPhotos:
      type === 'PHOTO' && item.required
        ? Math.max(1, item.minPhotos ?? 1)
        : item.photoRequired
          ? Math.max(1, item.minPhotos ?? 1)
          : 0,
    config: Object.keys(config).length ? config : null,
  };
}
